// LAYOUT-SPEC §0 — Deliverables (the program's deliverable list)
//
//   ONE QUESTION  What does this contract require us to deliver, and which of
//                 those need attention?
//   PRIMARY OBJECT  A collection of deliverables (Archetype B).
//   PRIMARY VERB    Open a deliverable. New deliverable is the one create.
//   WORST-CASE VOLUME  ~100 deliverables (OMEGA: 92). One page, client filters.
//
// Numbers per row come from ListOccurrences over the current contract period
// (the next date and its state); "needs a start date" from ListDuties'
// check codes, so the list never calls GetDuty per row.
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { SpecraBadge, SpecraButton, SpecraEmptyState, SpecraEntityCell, SpecraFormField, SpecraInput, SpecraListPage, SpecraPageHeader, SpecraSectionHeader, SpecraSegmentedControl, SpecraSelect, SpecraText, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { ClipboardList } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useProgramContext } from '../../../../layouts/ProgramLayout';
import { apiErrorMessage } from '../../../../lib/api/apiErrorMessage';
import { EmptyState } from '../../_shared/components/PageStates';
import { GlossaryText, useGlossaryCasing } from '../../Glossary/GlossaryText';
import { useContractCLINs } from '../../Programs/contractHooks';
import { useCmAccess } from '../access';
import { AwaitingApprovalBanner, AwaitingApprovalView } from '../components/AwaitingApproval';
import { CmDrawer } from '../components/CmDrawer';
import { clearAllAction, useColumnView } from '../components/ColumnMenu';
import { CmStateTable, CmStatusBadge, CmTable, routedLink, SowSilentLink } from '../components/common';
import { UpcomingDeliverables } from '../components/UpcomingDeliverables';
import { useCmDrawerParams } from '../drawerParams';
import { OccurrenceWindow, useCreateDuty, useOccurrences, useUpdateDuty } from '../hooks';
import { currentClinLabel } from '../lib/dutyClin';
import { deliverableTeamIds } from '../lib/dutyOwnership';
import { pwsChipLabel } from '../lib/dutySource';
import { cmDate } from '../lib/format';
import { describeRule, dutyTaskNumber, groupRules, loggableEvents } from '../lib/rule';
import { CONDITION_NOT_SETTLED, conditionHeldStatus, isOccurrenceState, LATE_OR_APPROVED_LATE, nextDue, STATE_SHORT_LABEL, submissionProgress, tierLabel } from '../lib/status';
import { filterDeliverableRows, normalizeTeamId, periodIdsForOccurrences } from '../lib/tableControls';
import { useCmLookups, useConditionHolds, useSowSilentQuestions } from '../lookups';
/** The PWS cell's text: the chip label without its leading "PWS " (the header says it). */ const pwsCellLabel = (r)=>pwsChipLabel(r.pwsRef, r.letter).replace(/^PWS /, '');
/** Lowercase, trimmed, whitespace-collapsed, dash-variants unified — so
 * "CLIN 0002 — Help Desk Support" and "clin 0002  help desk support" compare
 * equal regardless of which dash or how much whitespace the source used. */ function normalizeForMatch(s) {
    return s.toLowerCase().replace(/[—–-]/g, ' ').replace(/\s+/g, ' ').trim();
}
/**
 * A workstream/team name "relates to" a query if either contains the other.
 * The "query contains team name" direction is guarded to normalized names of
 * at least 3 characters, so a short team name (e.g. "IT") can't match nearly
 * any query by pure substring luck.
 */ function teamRelatesToQuery(teamName, q) {
    const normTeam = normalizeForMatch(teamName);
    const normQ = normalizeForMatch(q);
    if (!normTeam || !normQ) return false;
    if (normTeam.includes(normQ)) return true;
    return normTeam.length >= 3 && normQ.includes(normTeam);
}
/** ?awaiting=1 swaps the list for the proposals awaiting approval (D111). */ export function DeliverablesPage() {
    const { programId } = useProgramContext();
    const [sp] = useSearchParams();
    return sp.get('awaiting') ? /*#__PURE__*/ _jsx(AwaitingApprovalView, {
        programId: programId
    }) : /*#__PURE__*/ _jsx(DeliverablesList, {
        programId: programId
    });
}
/**
 * The page shell: header actions and the Upcoming / All switch (?view=), over
 * whichever view is showing. A ?state= deep-link is a section of the All view.
 */ function DeliverablesList({ programId }) {
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const lk = useCmLookups(programId);
    const [sp, setSp] = useSearchParams();
    const view = sp.get('state') || sp.get('view') === 'all' ? 'all' : 'upcoming';
    const [editing, setEditing] = useState(null);
    const header = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(SpecraPageHeader, {
                titleSize: "home",
                title: "Deliverables",
                meta: lk.duties.length,
                description: "What the contract requires, each with its rule and its next date.",
                actions: /*#__PURE__*/ _jsx("div", {
                    className: "flex gap-2",
                    children: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>setEditing('new'),
                        "data-testid": Testids.cmDuties.create,
                        children: "New deliverable"
                    }) : null
                })
            }),
            /*#__PURE__*/ _jsx(AwaitingApprovalBanner, {
                programId: programId
            }),
            /*#__PURE__*/ _jsx(SpecraSegmentedControl, {
                label: "View",
                value: view,
                options: [
                    {
                        value: 'upcoming',
                        label: 'Upcoming'
                    },
                    {
                        value: 'all',
                        label: 'All deliverables'
                    }
                ],
                onValueChange: (v)=>setSp((prev)=>{
                        const n = new URLSearchParams(prev);
                        if (v === 'all') n.set('view', 'all');
                        else n.delete('view');
                        return n;
                    }, {
                        replace: true
                    }),
                testId: Testids.cmDeliverablesView.toggle
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            view === 'all' ? /*#__PURE__*/ _jsx(AllDeliverables, {
                programId: programId,
                header: header,
                onNew: ()=>setEditing('new')
            }) : /*#__PURE__*/ _jsx(UpcomingDeliverables, {
                programId: programId,
                header: header
            }),
            editing ? /*#__PURE__*/ _jsx(DutyFormDrawer, {
                programId: programId,
                duty: editing === 'new' ? undefined : editing,
                onClose: ()=>setEditing(null),
                onSaved: (id)=>{
                    setEditing(null);
                    if (editing === 'new') navigate(`/delivery/programs/${programId}/deliverables/${id}`);
                }
            }) : null
        ]
    });
}
function AllDeliverables({ programId, header, onNew }) {
    const navigate = useNavigate();
    const access = useCmAccess(programId);
    const params = useCmDrawerParams();
    const lk = useCmLookups(programId);
    const clinsQ = useContractCLINs(programId);
    const clins = clinsQ.data?.clins ?? [];
    const [sp, setSp] = useSearchParams();
    const stateFilter = sp.get('state') ?? '';
    const periodFilter = sp.get('period') ?? '';
    const query = sp.get('q') ?? '';
    const occs = useOccurrences(programId, {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        includeProjected: true
    });
    // SAD-1301: on a period changeover the current period may hold none of a
    // deliverable's dates; the contract-wide dates are the fallback.
    const contractOccs = useOccurrences(programId, {
        window: OccurrenceWindow.ALL,
        includeProjected: true
    });
    const lateOrApprovedLate = stateFilter === LATE_OR_APPROVED_LATE;
    const filterStates = lateOrApprovedLate ? [
        'late',
        'approved'
    ] : [
        stateFilter
    ];
    const stateQ = useOccurrences(programId, periodFilter === 'all' ? {
        window: OccurrenceWindow.ALL,
        states: filterStates
    } : periodFilter ? {
        window: OccurrenceWindow.CONTRACT_PERIOD,
        periodOfPerformanceId: periodFilter,
        states: filterStates
    } : {
        window: OccurrenceWindow.CONTRACT_CURRENT_PERIOD,
        states: filterStates
    }, {
        enabled: !!stateFilter
    });
    // Approved dates count only when filed after their due date, as the Late tile does.
    const stateRows = useMemo(()=>{
        const all = stateQ.data?.occurrences ?? [];
        const occurrences = lateOrApprovedLate ? all.filter((o)=>o.status?.state === 'late' || o.status?.state === 'approved' && o.status.timingKnown && !o.status.onTime) : all;
        return {
            occurrences,
            total: lateOrApprovedLate ? occurrences.length : stateQ.data?.total ?? 0
        };
    }, [
        stateQ.data,
        lateOrApprovedLate
    ]);
    const holdOf = useConditionHolds(programId, [
        ...lk.checkCodes.values()
    ].some((c)=>c.includes(CONDITION_NOT_SETTLED)));
    // SAD-969: open "SOW doesn't mention this" questions, by deliverable.
    const silentOf = useSowSilentQuestions(programId);
    const rows = useMemo(()=>{
        const currentPeriodId = occs.data?.window?.periodOfPerformanceId;
        const currentOptionNumber = currentPeriodId ? lk.periodById.get(currentPeriodId)?.optionNumber : undefined;
        const byDuty = new Map();
        for (const o of occs.data?.occurrences ?? []){
            const arr = byDuty.get(o.dutyId) ?? [];
            arr.push(o);
            byDuty.set(o.dutyId, arr);
        }
        const byDutyAll = new Map();
        for (const o of contractOccs.data?.occurrences ?? []){
            const arr = byDutyAll.get(o.dutyId) ?? [];
            arr.push(o);
            byDutyAll.set(o.dutyId, arr);
        }
        return lk.duties.map((duty)=>{
            const rules = (lk.rulesByDuty.get(duty.id) ?? []).filter((r)=>!r.assignmentId);
            // A rule a schedule prints once per period is one rule here (SAD-851).
            const groups = groupRules(rules, lk.rulePlace);
            const checks = lk.checkCodes.get(duty.id) ?? [];
            const list = byDuty.get(duty.id) ?? [];
            const next = nextDue(list, byDutyAll.get(duty.id) ?? []);
            // Ownership matches the Contract-wide team page (award rules / gates),
            // not only dated occurrences in the current window.
            const allRules = lk.rulesByDuty.get(duty.id) ?? [];
            const datedTeamIds = new Set(list.map((o)=>normalizeTeamId(o.workstreamId)));
            const teamIds = deliverableTeamIds(datedTeamIds, duty, allRules, occs.data?.window);
            return {
                duty,
                title: duty.title,
                dutyKey: duty.dutyKey,
                clinLabel: currentClinLabel(duty.dutyKey, clins, currentOptionNumber),
                pwsRef: duty.pwsRef,
                letter: duty.letter,
                origin: duty.origin,
                sourceDocumentId: duty.sourceDocumentId,
                teamIds,
                datedTeamIds,
                periodIds: new Set(periodIdsForOccurrences(list, lk.periods, lk.exercises)),
                nextDueOn: next?.dueOn,
                ruleText: groups.length ? describeRule(groups[0].rules[0], (id)=>lk.dutyById.get(id)?.title) + (groups.length > 1 ? ` (+${groups.length - 1} more)` : '') : '',
                needsStart: checks.includes('needs_start_date'),
                held: checks.includes(CONDITION_NOT_SETTLED) ? conditionHeldStatus(holdOf(duty.pwsRef)) : undefined,
                checks,
                next,
                progress: submissionProgress(list.length ? list : byDutyAll.get(duty.id) ?? []),
                task: dutyTaskNumber(rules, lk.rulePlace)
            };
        }).sort((a, b)=>a.duty.title.localeCompare(b.duty.title));
    }, [
        lk.duties,
        lk.dutyById,
        lk.rulesByDuty,
        lk.checkCodes,
        lk.rulePlace,
        lk.periods,
        lk.exercises,
        lk.periodById,
        clins,
        occs.data,
        contractOccs.data,
        holdOf
    ]);
    const searched = useMemo(()=>{
        // Team-name search (SAD-656) sits beside the shared text match. Match dated
        // occurrence teams only: free-text "Contract-wide" finds team-less rows, not
        // every deliverable the contract-wide page lists by ownership.
        const q = query.trim();
        return q ? rows.filter((r)=>{
            const teamMatch = [
                ...r.datedTeamIds
            ].some((id)=>teamRelatesToQuery(lk.teamName(id), query));
            if (teamMatch) return true;
            return filterDeliverableRows([
                r
            ], {
                q
            }, clins).length > 0;
        }) : rows;
    }, [
        rows,
        query,
        clins,
        lk
    ]);
    const specs = [
        {
            id: 'title',
            label: 'Deliverable',
            value: (r)=>r.title
        },
        {
            id: 'source',
            label: 'CLIN',
            value: (r)=>r.clinLabel
        },
        {
            id: 'pws',
            label: 'PWS',
            value: (r)=>pwsCellLabel(r)
        },
        {
            id: 'rule',
            label: 'Rule',
            value: (r)=>r.ruleText
        },
        {
            id: 'next',
            label: 'Next due',
            kind: 'date',
            value: (r)=>r.next ? cmDate(r.next.dueOn, '—') : '—',
            sortValue: (r)=>r.next?.dueOn ?? ''
        }
    ];
    const view = useColumnView(specs, rows);
    const visible = view.apply(searched);
    // SAD-969 / SAD-975: the SOW-silent question, and where a team's SOW routes it.
    const marks = (duty)=>{
        const silent = silentOf(duty.id)[0];
        const routed = (lk.routingsByDuty.get(duty.id) ?? []).map((rt)=>/*#__PURE__*/ _jsxs("span", {
                className: "inline-flex flex-wrap items-center gap-1.5",
                children: [
                    routedLink(programId, rt.fromWorkstreamId, `Routed from ${lk.teamName(rt.fromWorkstreamId)}`, Testids.cmDuties.routedFrom),
                    routedLink(programId, rt.toWorkstreamId, `Routed to ${lk.teamName(rt.toWorkstreamId)}`, Testids.cmDuties.routedTo)
                ]
            }, rt.sowId));
        return silent || routed.length ? /*#__PURE__*/ _jsxs("div", {
            className: "flex flex-wrap items-center gap-1.5",
            children: [
                silent ? /*#__PURE__*/ _jsx(SowSilentLink, {
                    href: silent.href,
                    testId: Testids.cmDuties.sowSilent
                }) : null,
                routed
            ]
        }) : null;
    };
    const columns = [
        {
            id: 'title',
            header: view.header('title'),
            cell: (r)=>/*#__PURE__*/ _jsx(SpecraEntityCell, {
                    title: /*#__PURE__*/ _jsx(GlossaryText, {
                        text: r.duty.title
                    }),
                    description: r.duty.endedOn ? `Ended ${cmDate(r.duty.endedOn)}` : undefined,
                    trailing: r.task !== null ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                        content: `Owed under each exercise of Optional Task ${r.task}, counted from that exercise's PoP; no dates until it is exercised.`,
                        children: /*#__PURE__*/ _jsx("span", {
                            children: /*#__PURE__*/ _jsxs(SpecraBadge, {
                                variant: "neutral",
                                "data-testid": Testids.cmDuties.taskTag,
                                children: [
                                    "Optional Task ",
                                    r.task
                                ]
                            })
                        })
                    }) : undefined
                })
        },
        {
            id: 'source',
            header: view.header('source'),
            cell: (r)=>r.clinLabel ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "info",
                    className: "tabular-nums",
                    "data-testid": Testids.cmDuties.sourceClin,
                    children: r.clinLabel
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                })
        },
        {
            id: 'pws',
            header: view.header('pws'),
            cell: (r)=>pwsCellLabel(r) ? /*#__PURE__*/ _jsx(SpecraBadge, {
                    variant: "neutral",
                    className: "tabular-nums",
                    "data-testid": Testids.cmDuties.sourcePws,
                    children: pwsCellLabel(r)
                }) : /*#__PURE__*/ _jsx("span", {
                    className: "text-muted-foreground",
                    children: "—"
                })
        },
        {
            id: 'rule',
            header: view.header('rule'),
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    "data-testid": Testids.cmDuties.ruleCell,
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: r.ruleText ? '' : 'text-muted-foreground',
                            children: r.ruleText ? /*#__PURE__*/ _jsx(GlossaryText, {
                                text: r.ruleText
                            }) : 'No rule'
                        }),
                        r.needsStart ? /*#__PURE__*/ _jsx(SpecraTooltip, {
                            content: "This rule has no first date, so no dates exist yet.",
                            children: /*#__PURE__*/ _jsx("span", {
                                children: /*#__PURE__*/ _jsx(SpecraBadge, {
                                    variant: "warning",
                                    "data-testid": Testids.cmDuties.needsStartBadge,
                                    children: "Needs a start date"
                                })
                            })
                        }) : null,
                        r.held ? /*#__PURE__*/ _jsx("div", {
                            children: /*#__PURE__*/ _jsx(CmStatusBadge, {
                                status: r.held,
                                testId: Testids.cmDuties.conditionHeld
                            })
                        }) : null,
                        marks(r.duty)
                    ]
                })
        },
        {
            id: 'next',
            header: view.header('next'),
            align: 'right',
            cell: (r)=>/*#__PURE__*/ _jsxs("div", {
                    className: "whitespace-nowrap tabular-nums",
                    children: [
                        r.next ? cmDate(r.next.dueOn, '—') : '—',
                        r.progress.submitted + r.progress.toGo > 0 ? /*#__PURE__*/ _jsxs("div", {
                            className: "text-xs text-muted-foreground",
                            "data-testid": Testids.cmDuties.progress,
                            children: [
                                r.progress.submitted,
                                " submitted · ",
                                r.progress.toGo,
                                " to go"
                            ]
                        }) : null
                    ]
                })
        }
    ];
    const empty = /*#__PURE__*/ _jsx("div", {
        "data-testid": Testids.cmDuties.empty,
        children: /*#__PURE__*/ _jsx(EmptyState, {
            icon: ClipboardList,
            title: "No deliverables yet",
            description: "The award’s deliverables appear here after intake; add one by hand now.",
            action: access.canCreate ? /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: onNew,
                children: "New deliverable"
            }) : undefined
        })
    });
    const clearState = ()=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            n.delete('state');
            n.delete('period');
            return n;
        });
    const clearQuery = ()=>setSp((prev)=>{
            const n = new URLSearchParams(prev);
            n.delete('q');
            return n;
        });
    const noMatch = /*#__PURE__*/ _jsx(SpecraEmptyState, {
        framed: true,
        title: `No deliverables match “${query}”`,
        description: "Search looks at the title, PWS reference, deliverable number and team.",
        action: /*#__PURE__*/ _jsx(SpecraButton, {
            size: "sm",
            variant: "ghost",
            onClick: clearQuery,
            "data-testid": Testids.cmDuties.noMatchClear,
            children: "Clear search"
        })
    });
    return /*#__PURE__*/ _jsx(SpecraListPage, {
        "data-testid": Testids.cmDuties.page,
        header: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                header,
                stateFilter ? /*#__PURE__*/ _jsxs("section", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraSectionHeader, {
                            variant: "group",
                            title: /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    lateOrApprovedLate ? 'Late, incl. approved late' : isOccurrenceState(stateFilter) ? STATE_SHORT_LABEL[stateFilter] : stateFilter,
                                    periodFilter === 'all' ? ' · All periods' : periodFilter ? ` · ${lk.periodById.get(periodFilter)?.name ?? 'past period'}` : ' · this period',
                                    ' ',
                                    "· ",
                                    stateRows.total
                                ]
                            }),
                            action: /*#__PURE__*/ _jsx(SpecraButton, {
                                size: "sm",
                                variant: "ghost",
                                onClick: clearState,
                                children: "Clear"
                            })
                        }),
                        /*#__PURE__*/ _jsx(CmStateTable, {
                            programId: programId,
                            occurrences: stateRows.occurrences,
                            dutyTitle: (id)=>lk.dutyById.get(id)?.title,
                            finalTierOf: (id)=>lk.dutyById.get(id)?.finalReviewTier,
                            teamName: lk.teamName,
                            onOpen: (id)=>params.open('occ', id),
                            isLoading: stateQ.isLoading,
                            empty: /*#__PURE__*/ _jsx(SpecraEmptyState, {
                                size: "compact",
                                title: "Nothing in this state.",
                                description: "No date in this period is in it right now."
                            })
                        })
                    ]
                }) : null
            ]
        }),
        toolbarProps: {
            search: {
                value: query,
                onChange: (e)=>setSp((prev)=>{
                        const n = new URLSearchParams(prev);
                        if (e.target.value) n.set('q', e.target.value);
                        else n.delete('q');
                        return n;
                    }, {
                        replace: true
                    }),
                onClear: clearQuery,
                placeholder: 'Search deliverables…',
                'data-testid': Testids.cmDuties.search
            },
            resultSummary: visible.length === rows.length ? `${rows.length} total` : `${visible.length} of ${rows.length} shown`
        },
        children: /*#__PURE__*/ _jsx(CmTable, {
            caption: "Deliverables",
            rows: visible,
            columns: columns,
            rowKey: (r)=>r.duty.id,
            rowTestId: Testids.cmDuties.row,
            isLoading: lk.isLoading,
            onRowClick: (r)=>navigate(`/delivery/programs/${programId}/deliverables/${r.duty.id}`),
            rowChevron: true,
            getRowLabel: (r)=>r.duty.title,
            empty: rows.length === 0 ? empty : query ? noMatch : /*#__PURE__*/ _jsx(SpecraEmptyState, {
                framed: true,
                title: "No deliverables match these filters",
                description: "The filters hide every deliverable.",
                action: clearAllAction(view)
            })
        })
    });
}
/**
 * A hand-added deliverable's identity key: its PWS pointer when stated, else
 * its title, plus a short random tail so two hand entries never collide. The
 * award's own deliverables get their CLIN-family key from intake (D107).
 */ export function manualDutyKey(pwsRef, letter, title) {
    const base = (pwsRef.trim() ? `pws-${pwsRef.trim()}${letter.trim() ? `-${letter.trim()}` : ''}` : title).toLowerCase().replace(/[^a-z0-9.]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 48);
    return `manual:${base || 'deliverable'}:${Math.random().toString(36).slice(2, 8)}`;
}
/** New / edit a deliverable (its identity fields; its rules live on its page). */ export function DutyFormDrawer({ programId, duty, onClose, onSaved }) {
    const [title, setTitle] = useState(duty?.title ?? '');
    const [pwsRef, setPwsRef] = useState(duty?.pwsRef ?? '');
    const [letter, setLetter] = useState(duty?.letter ?? '');
    const [tier, setTier] = useState(duty?.finalReviewTier || 'va_pm');
    const [locator, setLocator] = useState(duty?.sourceLocator ?? '');
    const [eventKind, setEventKind] = useState(duty?.eventKind ?? '');
    const lk = useCmLookups(programId);
    const casing = useGlossaryCasing();
    // A kind another deliverable already dates is not offered: one per event.
    const events = loggableEvents(lk.rules, lk.duties, casing).filter((e)=>e.kind === duty?.eventKind || !lk.duties.some((d)=>d.eventKind === e.kind));
    const create = useCreateDuty(programId, {
        success: 'Deliverable added',
        errors: 'inline'
    });
    const update = useUpdateDuty(programId, {
        success: 'Deliverable saved',
        errors: 'inline'
    });
    const busy = create.isPending || update.isPending;
    const error = create.error ?? update.error;
    const save = ()=>{
        const fields = {
            ...duty ?? {},
            title: title.trim(),
            pwsRef: pwsRef.trim(),
            letter: letter.trim(),
            finalReviewTier: tier,
            sourceLocator: locator.trim(),
            eventKind
        };
        if (duty) update.mutate({
            dutyId: duty.id,
            fields
        }, {
            onSuccess: ()=>onSaved(duty.id)
        });
        else create.mutate({
            fields,
            origin: 'manual',
            dutyKey: manualDutyKey(pwsRef, letter, title)
        }, {
            onSuccess: (r)=>r.duty && onSaved(r.duty.id)
        });
    };
    return /*#__PURE__*/ _jsx(CmDrawer, {
        title: duty ? 'Edit deliverable' : 'New deliverable',
        description: duty ? undefined : 'Add a deliverable the contract requires. Its rule comes next.',
        onClose: onClose,
        busy: busy,
        testId: Testids.cmDuties.createDrawer,
        footerProps: {
            confirmLabel: duty ? 'Save' : 'Add deliverable',
            onConfirm: save,
            confirmDisabled: !title.trim() || busy,
            loading: busy,
            onCancel: onClose,
            confirmProps: {
                'data-testid': Testids.cmDuties.createSave
            }
        },
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Title",
                    required: true,
                    hint: "As the contract names it.",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: title,
                        onChange: (e)=>setTitle(e.target.value),
                        "data-testid": Testids.cmDuties.createTitle
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "grid grid-cols-1 sm:grid-cols-2 gap-3",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "PWS reference",
                            hint: "e.g. 5.4.7.1",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: pwsRef,
                                onChange: (e)=>setPwsRef(e.target.value),
                                "data-testid": Testids.cmDuties.createPwsRef
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Letter",
                            hint: "e.g. A",
                            children: /*#__PURE__*/ _jsx(SpecraInput, {
                                value: letter,
                                onChange: (e)=>setLetter(e.target.value)
                            })
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Final review",
                    hint: "The government review that approves it. A contractor review never counts.",
                    children: /*#__PURE__*/ _jsx(SpecraSelect, {
                        value: tier,
                        onChange: (e)=>setTier(e.target.value),
                        "data-testid": Testids.cmDuties.createTier,
                        children: [
                            'va_approver',
                            'va_pm',
                            'va_cor'
                        ].map((t)=>/*#__PURE__*/ _jsx("option", {
                                value: t,
                                children: tierLabel(t)
                            }, t))
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Where the contract says so",
                    hint: "e.g. “Award PWS 5.4.7.1, p.66”",
                    children: /*#__PURE__*/ _jsx(SpecraInput, {
                        value: locator,
                        onChange: (e)=>setLocator(e.target.value)
                    })
                }),
                /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Dates an event",
                    hint: "e.g. “Technical kickoff meeting within 10 business days of award”: logging the meeting meets this date, and the deliverables due after the meeting are projected from it until then.",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        value: eventKind,
                        onChange: (e)=>setEventKind(e.target.value),
                        "data-testid": Testids.cmDuties.createEventKind,
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "No event"
                            }),
                            events.map((e)=>/*#__PURE__*/ _jsx("option", {
                                    value: e.kind,
                                    children: e.label
                                }, e.kind))
                        ]
                    })
                }),
                error ? /*#__PURE__*/ _jsx(SpecraText, {
                    as: "p",
                    size: "caption",
                    tone: "danger",
                    role: "alert",
                    children: apiErrorMessage(error, 'Could not save')
                }) : null
            ]
        })
    });
}
